import { Suspense, memo, useMemo, type VFC, useState } from 'react'
import { Banner, Button } from '@panwds/react-ui'
import { hasExposedNamespace, lazyImportComponent } from '../../framework/microApps'
import { CSP_DOWN_BANNER_LABEL, CSP_DOWN_BUTTON_LABEL } from '../../framework/constants'
import NavErrorBoundary from '../common/NavErrorBoundary'
import { useRootElementClassNameLayoutEffect } from '../common/appEffects'
import type { AuthState, FrameworkVars, StateRef } from '../../framework/stateTypes'
import type { Location } from 'history'

const LazyComp = lazyImportComponent('scm_main_layout.content_top', 'MSPTenantBreadcrumbs')
const MSPTenantBreadcrumbs: VFC<{ auth: AuthState, fxVars: FrameworkVars, location: Location }> = ({ auth, fxVars }) => {
  const hidden = useMemo(() => (
    !fxVars.msp_top_breadcrumbs || (fxVars.msp_top_breadcrumbs === 'msp-only' && !fxVars.msp) ||
    !auth.tsgs.hasMsp || !auth.tsgs.hasParentOrSubTenants(auth.tsg_id)
  ), [auth.tsg_id, auth.tsgs, fxVars.msp, fxVars.msp_top_breadcrumbs])

  useRootElementClassNameLayoutEffect(hidden ? '' : 'scm-with-content-top')

  return hidden ? null : (
    <Suspense fallback={null}>
      <aside className='twsf-flex-0 empty:twsf-hidden'>
        <LazyComp auth={auth} />
      </aside>
    </Suspense>
  )
}

const NavTopBar: VFC<{ stateRef: StateRef, auth: AuthState, fxVars: FrameworkVars, location: Location }> = ({ auth, fxVars, location }) => {
  const cspDown = auth.user?.cspNotAvailable && !auth.loading
  const [cspDownBannerClosed, setCspDownBannerClosed] = useState(false)
  const cspHeader = (cspDown && !cspDownBannerClosed) ? (
    <div className='twsf-absolute twsf-inset-0 twsf-left-0 twsf-right-0 twsf-top-0 twsf-z-[100] twsf-w-full twsf-h-0'>
      <Banner
        actions={<Button appearance='tertiary' size='sm'>
          <a href='https://status.paloaltonetworks.com/' rel='noreferrer noopener' target='_blank'>
            {CSP_DOWN_BUTTON_LABEL}
          </a>
        </Button>}
        appearance='warning'
        onClose={() => {
          setCspDownBannerClosed(true)
        }}
        showIcon
        type='page'
      >
        {CSP_DOWN_BANNER_LABEL}
      </Banner>
    </div>
  ) : null
  const mspHeader = hasExposedNamespace('scm_main_layout.content_top') ? (
    <NavErrorBoundary context='scm_main_layout.content_top'>
      <MSPTenantBreadcrumbs auth={auth} fxVars={fxVars} location={location} />
    </NavErrorBoundary>
  ) : null
  return (
    <>
      {cspHeader}
      {mspHeader}
    </>
  )
}

export default memo(NavTopBar)
